import { confirm, DataTable, type TanstackReactTable } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { EditIcon, TrashIcon } from "lucide-react";
import { RowActions } from "@/shared/ui/RowActions";
import { formatConfigurationPeriodRange } from "@/shared/lib/vehicle-configuration-period";
import type { VehicleConfigurationLookupItem } from "../api/client";

type VehicleConfigurationsTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: VehicleConfigurationLookupItem[];
  offset: number;
  limit: number;
  totalCount: number;
  onEdit: (row: VehicleConfigurationLookupItem) => void;
  onDelete: (id: string) => Promise<void>;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function VehicleConfigurationsTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  onEdit,
  onDelete,
  onPaginationChange,
}: VehicleConfigurationsTableProps) {
  const columns: TanstackReactTable.ColumnDef<VehicleConfigurationLookupItem>[] =
    [
      {
        id: "order",
        header: "ລຳດັບ",
        cell: ({ row }) => offset + row.index + 1,
        size: 70,
      },
      {
        id: "modelCode",
        header: "Model code",
        cell: ({ row }) => (
          <span className="font-mono text-sm">
            {row.original.modelCode ?? "—"}
          </span>
        ),
        size: 100,
      },
      {
        id: "years",
        header: "ຊ່ວງປີ",
        cell: ({ row }) =>
          formatConfigurationPeriodRange(
            row.original.yearFrom,
            row.original.yearTo,
            row.original.monthFrom,
            row.original.monthTo,
          ) ?? "—",
        size: 120,
      },
      {
        id: "engine",
        header: "Engine",
        cell: ({ row }) => row.original.engine ?? "—",
        size: 100,
      },
      {
        id: "market",
        header: "Market / Trans",
        cell: ({ row }) =>
          [row.original.marketCode, row.original.transmissionCode]
            .filter(Boolean)
            .join(" · ") || "—",
        size: 120,
      },
      {
        id: "adminNotes",
        header: "ໝາຍເຫດ",
        cell: ({ row }) => row.original.adminNotes ?? "—",
        size: 180,
      },
      {
        id: "epc",
        header: "EPC",
        cell: ({ row }) => (
          <Link
            className="text-primary underline decoration-primary/60 underline-offset-2 hover:decoration-primary"
            to="/app/catalog/vehicles/$makeId/models/$modelId/configurations/$configurationId/epc"
            params={{
              makeId: row.original.makeId,
              modelId: row.original.modelId,
              configurationId: row.original.id,
            }}
          >
            ເປີດ
          </Link>
        ),
        size: 80,
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          if (!canManage) return null;
          const id = row.original.id;
          const actions = [
            {
              label: "ແກ້ໄຂ",
              icon: <EditIcon className="size-4" />,
              onClick: () => onEdit(row.original),
            },
            {
              label: "ລຶບ",
              variant: "destructive" as const,
              icon: <TrashIcon className="size-4" />,
              onClick: async () => {
                const ok = await confirm({
                  title: "ລຶບ configuration",
                  description:
                    "ຖ້າມີ fitment ຜູກຢູ່ ຈະລຶບບໍ່ໄດ້. ຕ້ອງການລຶບບໍ?",
                  actionText: "ລຶບ",
                  ActionProps: { variant: "destructive" },
                });
                if (ok) await onDelete(id);
              },
            },
          ];
          return <RowActions actions={actions} maxInline={2} />;
        },
        size: 120,
      },
    ];

  return (
    <DataTable<VehicleConfigurationLookupItem, unknown>
      noDataMessage="ບໍ່ມີ configuration — ເພີ່ມເພື່ອກຳນົດປີ / engine / codes"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
